import React from 'react';

// Deterministic cashflow charts for the portfolio /analyze result.
// Inline SVG, no chart library. Values are derived from the same period
// fields shown in the cashflow table.

const fmtEur = (v: number) => '€' + Math.round(v).toLocaleString();

type Period = any; // a cashflow_breakdown item

interface Bucket {
  label: string;
  income: number;       // revenue (positive)
  expenses: number;     // operating expenses (negative)
  net: number;          // income + expenses (operating)
  cumulative: number;   // running full-economic total through this bucket
  isPurchase: boolean;  // a purchase one-off lands here
  isSale: boolean;      // sale proceeds land here
  hasTax: boolean;      // income tax / levy / CGT charged here
}

// Full after-tax economic result for a period: operating cashflow + one-off
// costs + sale proceeds (= earnings_before_tax) plus taxes (negative).
const periodTotal = (p: Period): number =>
  (p.earnings_before_tax || 0) + (p.french_income_tax || 0) + (p.french_social_levy || 0)
  + (p.uk_income_tax_eur || 0) + (p.french_cgt_surtax || 0);

const taxAmount = (p: Period): number =>
  (p.french_income_tax || 0) + (p.french_social_levy || 0)
  + (p.uk_income_tax_eur || 0) + (p.french_cgt_surtax || 0);

// Collapse periods into at most maxBuckets groups (so daily granularity over
// many years stays responsive); cumulative is the running total at the end of
// each group, flags are OR-ed across the group.
function bucketize(periods: Period[], maxBuckets: number): Bucket[] {
  if (!periods || periods.length === 0) return [];
  let cum = 0;
  const rows = periods.map((p) => {
    cum += periodTotal(p);
    return {
      label: p.date,
      income: p.revenue || 0,
      expenses: p.total_expenses || 0,
      cumulative: cum,
      tax: taxAmount(p),
      isPurchase: (p.deposit_outflow || 0) < 0 || (p.notaire_fees || 0) < 0,
      isSale: (p.sale_proceeds || 0) > 0,
    };
  });

  const toBucket = (grp: typeof rows): Bucket => ({
    label: grp[0].label,
    income: grp.reduce((a, e) => a + e.income, 0),
    expenses: grp.reduce((a, e) => a + e.expenses, 0),
    net: grp.reduce((a, e) => a + e.income + e.expenses, 0),
    cumulative: grp[grp.length - 1].cumulative,
    isPurchase: grp.some((e) => e.isPurchase),
    isSale: grp.some((e) => e.isSale),
    hasTax: grp.some((e) => Math.abs(e.tax) > 0.5),
  });

  if (rows.length <= maxBuckets) return rows.map((r) => toBucket([r]));
  const size = Math.ceil(rows.length / maxBuckets);
  const out: Bucket[] = [];
  for (let i = 0; i < rows.length; i += size) out.push(toBucket(rows.slice(i, i + size)));
  return out;
}

const xTicks = (n: number, count = 7): number[] => {
  if (n <= count) return Array.from({ length: n }, (_, i) => i);
  return Array.from({ length: count }, (_, i) => Math.round((i / (count - 1)) * (n - 1)));
};

export const CumulativeCashflowChart: React.FC<{ periods: Period[] }> = ({ periods }) => {
  const b = bucketize(periods, 600);
  if (!b.length) return null;
  const W = 760, H = 300, padL = 84, padR = 16, padT = 14, padB = 34;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const cums = b.map((x) => x.cumulative);
  const yMin = Math.min(0, ...cums), yMax = Math.max(0, ...cums);
  const x = (i: number) => padL + (b.length === 1 ? plotW / 2 : (i / (b.length - 1)) * plotW);
  const y = (v: number) => padT + plotH - ((v - yMin) / ((yMax - yMin) || 1)) * plotH;
  const linePts = b.map((bk, i) => `${x(i)},${y(bk.cumulative)}`).join(' ');
  const areaPts = `${x(0)},${y(0)} ${linePts} ${x(b.length - 1)},${y(0)}`;
  const zeroY = y(0);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <polygon points={areaPts} fill="#14b8a6" opacity={0.12} />
      {yMin < 0 && yMax > 0 && (
        <line x1={padL} y1={zeroY} x2={W - padR} y2={zeroY} stroke="#ef4444" strokeWidth={1} strokeDasharray="3 3" />
      )}
      <polyline points={linePts} fill="none" stroke="#0d9488" strokeWidth={2} />
      {b.map((bk, i) => bk.isPurchase && (
        <g key={`pu${i}`}>
          <line x1={x(i)} y1={padT} x2={x(i)} y2={padT + plotH} stroke="#6b7280" strokeWidth={1} strokeDasharray="4 3" />
          <circle cx={x(i)} cy={y(bk.cumulative)} r={3.5} fill="#6b7280" />
        </g>
      ))}
      {b.map((bk, i) => bk.isSale && (
        <circle key={`sa${i}`} cx={x(i)} cy={y(bk.cumulative)} r={4.5} fill="#16a34a" stroke="#fff" strokeWidth={1} />
      ))}
      {b.map((bk, i) => bk.hasTax && (
        <circle key={`tx${i}`} cx={x(i)} cy={padT + plotH + 6} r={2.5} fill="#f59e0b" />
      ))}
      <text x={4} y={y(yMax) + 9} fontSize="11" fill="#6b7280">{fmtEur(yMax)}</text>
      <text x={4} y={zeroY + 3} fontSize="11" fill="#9ca3af">€0</text>
      <text x={4} y={y(yMin)} fontSize="11" fill="#6b7280">{fmtEur(yMin)}</text>
      {xTicks(b.length).map((i) => (
        <text key={`xt${i}`} x={x(i)} y={H - 6} fontSize="10" fill="#6b7280" textAnchor="middle">{b[i].label}</text>
      ))}
    </svg>
  );
};

export const IncomeExpenseChart: React.FC<{ periods: Period[] }> = ({ periods }) => {
  const b = bucketize(periods, 120);
  if (!b.length) return null;
  const W = 760, H = 280, padL = 84, padR = 16, padT = 14, padB = 34;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const maxMag = Math.max(1, ...b.map((x) => Math.max(x.income, -x.expenses)));
  const n = b.length;
  const slot = plotW / n;
  const bw = Math.max(1, Math.min(slot * 0.8, 22));
  const zeroY = padT + plotH / 2;
  const scale = (plotH / 2) / maxMag;
  const cx = (i: number) => padL + slot * (i + 0.5);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full">
      <line x1={padL} y1={zeroY} x2={W - padR} y2={zeroY} stroke="#9ca3af" strokeWidth={1} />
      {b.map((bk, i) => (
        <g key={i}>
          <rect x={cx(i) - bw / 2} y={zeroY - bk.income * scale} width={bw} height={Math.max(0, bk.income * scale)} fill="#16a34a" opacity={0.85} />
          <rect x={cx(i) - bw / 2} y={zeroY} width={bw} height={Math.max(0, -bk.expenses * scale)} fill="#dc2626" opacity={0.8} />
        </g>
      ))}
      <polyline points={b.map((bk, i) => `${cx(i)},${zeroY - bk.net * scale}`).join(' ')}
        fill="none" stroke="#1d4ed8" strokeWidth={1.5} />
      <text x={4} y={padT + 9} fontSize="11" fill="#16a34a">{fmtEur(maxMag)}</text>
      <text x={4} y={zeroY + 3} fontSize="11" fill="#6b7280">€0</text>
      <text x={4} y={padT + plotH} fontSize="11" fill="#dc2626">-{fmtEur(maxMag)}</text>
      {xTicks(n).map((i) => (
        <text key={`xt${i}`} x={cx(i)} y={H - 6} fontSize="10" fill="#6b7280" textAnchor="middle">{b[i].label}</text>
      ))}
    </svg>
  );
};

const LegendDot: React.FC<{ color: string; label: string; dashed?: boolean }> = ({ color, label, dashed }) => (
  <span className="inline-flex items-center mr-4 text-xs text-gray-600">
    <span className="inline-block w-3 h-3 mr-1 rounded-sm" style={{ background: dashed ? 'transparent' : color, border: dashed ? `1px dashed ${color}` : 'none' }} />
    {label}
  </span>
);

export const CashflowCharts: React.FC<{ periods: Period[] }> = ({ periods }) => {
  if (!periods || periods.length === 0) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-1">Cumulative cashflow</h4>
        <p className="text-xs text-gray-500 mb-2">After-tax position over time, including purchase, sale and taxes.</p>
        <div className="mb-2">
          <LegendDot color="#6b7280" label="Purchase" dashed />
          <LegendDot color="#16a34a" label="Sale" />
          <LegendDot color="#f59e0b" label="Tax due" />
        </div>
        <CumulativeCashflowChart periods={periods} />
      </div>
      <div className="border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-1">Income vs expenses</h4>
        <p className="text-xs text-gray-500 mb-2">Operating revenue (green) and costs (red) per period; blue line is net.</p>
        <div className="mb-2">
          <LegendDot color="#16a34a" label="Income" />
          <LegendDot color="#dc2626" label="Expenses" />
          <LegendDot color="#1d4ed8" label="Net" />
        </div>
        <IncomeExpenseChart periods={periods} />
      </div>
    </div>
  );
};

export default CashflowCharts;
